import { useEffect, useState } from 'react';
import { useCookies } from 'react-cookie';
import { useTranslation } from 'react-i18next';
import { useSelector } from 'react-redux';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import { show } from '@intercom/messenger-js-sdk';
import { styled } from '@mui/material';
import { addYears } from 'date-fns';

import { authenticationAPIService } from '~/api/login';
import Logo from '~/assets/icons/sidebar-logo-new.svg?react';
import { BgImage } from '~/components/atoms/bg-element/BgImage';
import { Divider } from '~/components/ui/divider/Divider';
import { Text } from '~/components/ui/text';
import { TABLET_MIN_WIDTH } from '~/constants/common';
import { CookieNames } from '~/constants/cookie';
import { HTTP_STATUSES } from '~/constants/http-statuses-constants';
import { PATH } from '~/constants/route-path';
import { StyledContactSupport, StyledContactSupportContainer } from '~/pages/HomePage/components/LastStepLogin';
import { cookieService } from '~/services/cookie-service';
import { getIsDesktop, getIsMobile } from '~/store/reducers/app/selectors';
import { COLORS } from '~/styles/colors';
import { decodeJWT } from '~/utils/decodeJwt';

export default function Confirmation() {
  const navigate = useNavigate();
  const [, setCookie] = useCookies([CookieNames.userEmail]);

  const isAuthenticated = cookieService.getCookie('access') || null;

  const isDesktop = useSelector(getIsDesktop);
  const isMobile = useSelector(getIsMobile);

  const { t } = useTranslation();
  const [searchParams] = useSearchParams();

  const [error, setError] = useState(false);

  const microsteIdFromMagicLinkAnonymousQr = searchParams.get('microsite_id');
  const queryParams = {
    uid: searchParams.get('uid'),
    token: searchParams.get('token'),
    next: searchParams.get('next'),
  };
  const email = searchParams.get('email');

  const getNextLink = () => {
    if (queryParams.next) {
      return queryParams.next;
    }
    if (microsteIdFromMagicLinkAnonymousQr) {
      return `${PATH.QR.CREATE}?anonymous_microsite_id=${microsteIdFromMagicLinkAnonymousQr}`;
    }
    return PATH.QR.MAIN;
  };

  const loadAccountToken = async () => {
    try {
      // If we will want to logout user before loging in new one this is the place

      const res = await authenticationAPIService.getAccountToken(queryParams);

      if (res.code === HTTP_STATUSES.BAD_REQUEST) {
        setError(res?.message);

        return;
      }

      const { refresh, access, ...other } = res?.data || {};
      cookieService.setGroupOfCookies(other);
      if (access) {
        cookieService.setCookie('access', access, 365);
      }
      if (refresh) {
        cookieService.setCookie('refresh', refresh, 365);
      }

      if (email) {
        setCookie(CookieNames.userEmail, email, {
          path: '/',
          sameSite: 'lax',
          expires: addYears(new Date(), 1),
        });
      }
      const decodedToken = decodeJWT(res.data.access);
      const decodedUserId = decodedToken?.user_id;

      window.dataLayer?.push({
        event: 'sign_in',
        user_id: String(decodedUserId),
      });

      const nextLink = getNextLink();
      navigate(nextLink);
    } catch (e) {
      navigate('/login');
      setError(e?.response?.data?.non_field_errors?.[0]);
    }
  };

  useEffect(() => {
    if (isAuthenticated && queryParams.next) {
      navigate(queryParams.next);
    } else if (queryParams.uid && queryParams.token) {
      loadAccountToken();
    }
  }, [queryParams, isAuthenticated]);

  return (
    <StyledWrapper>
      {isDesktop && (
        <>
          <BgImage.circles $top={-200} $right={-180} $zIndex={0} />
          <BgImage.circles $top={300} $left={-180} $rotate={30} $zIndex={0} />
        </>
      )}
      <StyledContainer>
        {error && (
          <StyledErrorContainer>
            ⚠️
            <Text variant={isDesktop ? 't16Medium' : 'inputSmall'} sx={{ color: COLORS.white }}>
              {error}
            </Text>
          </StyledErrorContainer>
        )}
        <Link to={PATH.UNAUTH.MAIN}>
          <Logo width={395} height={88} />
        </Link>
        <Text variant={isDesktop ? 'h1.5' : 'h2'} sx={{ marginTop: isMobile ? '33px' : '66px', textAlign: 'center' }}>
          {t('confirmation:p1')}
        </Text>
        <Text
          variant={isDesktop ? 'subheader' : 'inputSmall'}
          sx={{ marginTop: '4px', color: COLORS.grey400, textAlign: 'center' }}
        >
          {t('login:confirmationh2')} {t('login:confirmationh3')}
        </Text>
        <Divider mb={24} mt={24} width={200} />
        <Text
          variant={isDesktop ? 'subheader' : 'inputSmall'}
          sx={{ color: COLORS.grey400, textAlign: 'center', marginBottom: '24px' }}
        >
          {t('confirmation:p4')}
        </Text>
        <StyledChangeEmailLink to={PATH.LOGIN}>{t('confirmation:btnText')}</StyledChangeEmailLink>
        <Text variant={isDesktop ? 't16Regular' : 'inputSmall'} sx={{ textAlign: 'center', marginTop: '24px' }}>
          {t('confirmation:p5')}
        </Text>
        <StyledContactSupportContainer>
          <Text variant={isDesktop ? 't16Regular' : 'inputSmall'} sx={{ textAlign: 'center' }}>
            {t('confirmation:p6')}
          </Text>
          <StyledContactSupport onClick={show}>{t('confirmation:p6_end_text')}</StyledContactSupport>
        </StyledContactSupportContainer>
      </StyledContainer>
    </StyledWrapper>
  );
}

export const StyledWrapper = styled('div')(() => {
  return {
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
    backgroundColor: COLORS.white,
    minHeight: '100dvh',
    position: 'relative',
    overflow: 'hidden',
  };
});

export const StyledContainer = styled('div')(({ theme }) => {
  return {
    width: '100%',
    maxWidth: '465px',
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    [theme.breakpoints.down(TABLET_MIN_WIDTH)]: {
      maxWidth: 'unset',
      padding: '16px',
    },
  };
});

export const StyledChangeEmailLink = styled(Link)(() => {
  return {
    width: '100%',
    borderRadius: '100px',
    backgroundColor: COLORS.primary,
    padding: '16px 45px',
    fontWeight: 500,
    fontSize: '18px',
    lineHeight: '25px',
    textAlign: 'center',
    color: COLORS.white,
    textDecoration: 'none',
  };
});

export const StyledErrorContainer = styled('div')(({ theme }) => {
  return {
    backgroundColor: COLORS.darkRed,
    borderRadius: '8px',
    padding: '10px 16px',
    minWidth: '262px',
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
    marginBottom: '60px',
    gap: '8px',
    [theme.breakpoints.down(TABLET_MIN_WIDTH)]: {
      minWidth: 'unset',
      with: '100%',
      marginBottom: '30px',
    },
  };
});
